import BagongPilipinasLogo from '@/assets/BAGONG-PILIPINAS-LOGO.png';
import DENRLogo from '@/assets/denr-logo.png';
import { format } from 'date-fns';
import { forwardRef } from 'react';
import QRCode from 'react-qr-code';

// Function to convert numbers to words (e.g., 1 to "one")
const numberToWords = (num) => {
   if (num === undefined || num === null) return '';

   const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
   const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

   if (num < 20) return ones[num];

   if (num < 100) {
      return tens[Math.floor(num / 10)] + (num % 10 ? '-' + ones[num % 10] : '');
   }

   if (num < 1000) {
      return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
   }

   return num.toString();
};

const TCEBPCertificateTemplate = forwardRef(({ certificate, application, orderOfPayment, hiddenOnPrint = [] }, ref) => {
   // Extract data from certificate or application
   const certificateData = certificate?.tcebpCertificateData || {
      ...application,
      name: application?.name,
      officeName: application?.officeName,
      officeAddress: application?.officeAddress,
      projectLocation: application?.projectLocation,
      contactNumber: application?.contactNumber,
      purpose: application?.purpose,
      requestType: application?.requestType,
      treeInventory: application?.findings?.tcebpFindings?.treeInventory || [],
      totalTrees: application?.findings?.tcebpFindings?.totalTrees || 0
   };

   console.log('TCEBP Template Data:', {
      certificateData,
      certificate,
      orderOfPayment
   });

   // Format total trees with number to words
   const totalTreesInWords = certificateData?.totalTrees ?
      `${numberToWords(certificateData.totalTrees).toUpperCase()} (${certificateData.totalTrees})` :
      '_______';

   // Use a direct URL instead of JSON data for better QR code compatibility across devices
   const qrValue = `${window.location.origin}/verify-certificate/${certificate?.id}`;

   const formatDate = (timestamp) => {
      try {
         if (!timestamp) return null;

         if (typeof timestamp === 'string') {
            if (!isNaN(timestamp)) {
               return format(new Date(parseInt(timestamp)), 'MMMM d, yyyy');
            }
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }

         if (typeof timestamp === 'number') {
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }

         if (timestamp instanceof Date) {
            return format(timestamp, 'MMMM d, yyyy');
         }

         return null;
      } catch (error) {
         console.error('Error formatting date:', error, 'timestamp:', timestamp);
         return null;
      }
   };

   return (
      <div ref={ref} className="p-8 bg-white">
         {/* Header */}
         <div className="text-center mb-8">
            <div className="flex justify-center items-center gap-4 mb-4">
               <img src={DENRLogo} alt="DENR Logo" className="h-20" />
               <div>
                  <h1 className="text-xl font-bold">TREE CUTTING AND/OR EARTH BALLING PERMIT</h1>
                  <p className="text-sm">Department of Environment and Natural Resources</p>
                  <p className="text-sm">PENRO Marinduque</p>
               </div>
               <img src={BagongPilipinasLogo} alt="GovPH Logo" className="h-20" />
            </div>
            <p className="text-sm">Permit No. {certificate?.certificateNumber || '_________________'}</p>
         </div>

         {/* Introduction */}
         <div className="mb-6">
            <p className="text-justify mb-4">
               Pursuant to DENR Administrative Order No. 2022-10 and existing forestry laws, rules, and regulations, a
               {certificateData?.requestType === 'Earth Balling' ? ' TREE EARTH-BALLING' : ' TREE CUTTING'} PERMIT is hereby granted to:
            </p>
         </div>

         {/* Permittee Details */}
         <div className="mb-6">
            <p className="font-bold text-center mb-2 text-blue-600 uppercase">{certificateData?.officeName || '_________________'}</p>
            <p className="text-center mb-4">represented by</p>
            <p className="font-bold text-center mb-2 text-blue-600">{certificateData?.name || '_________________'}</p>
            <p className="text-center mb-4">{certificateData?.officeAddress || '_________________'}</p>
            <p className="text-center mb-2">For the purpose of</p>
            <p className="font-bold text-center mb-4 text-blue-600">{certificateData?.purpose || '_________________'}</p>
            <p className="text-center mb-2">located at</p>
            <p className="font-bold text-center mb-4 text-blue-600">{certificateData?.projectLocation || '_________________'}</p>
         </div>

         {/* Tree Inventory Section */}
         <div className="mb-6">
            <p className="mb-4">
               Based on the inspection conducted by PENRO Marinduque Technical Personnel, a total of <span className="underline font-bold">{totalTreesInWords}</span> trees
               {certificateData?.requestType ? ` are authorized for ${certificateData.requestType.toLowerCase()}` : ''} as shown in the table below:
            </p>

            {/* Tree Inventory Table */}
            <table className="w-full border-collapse border border-black mb-4">
               <thead>
                  <tr>
                     <th className="border border-black p-2">Species</th>
                     <th className="border border-black p-2">No. of Trees</th>
                     <th className="border border-black p-2">Status</th>
                     <th className="border border-black p-2">Category</th>
                  </tr>
               </thead>
               <tbody>
                  {(certificateData?.treeInventory || []).length > 0 ? (
                     <>
                        {certificateData.treeInventory.map((tree, index) => (
                           <tr key={index}>
                              <td className="border border-black p-2 text-center">{tree.species}</td>
                              <td className="border border-black p-2 text-center">{tree.numberOfTreesInventoried}</td>
                              <td className="border border-black p-2 text-center">{tree.status}</td>
                              <td className="border border-black p-2 text-center">{tree.treeCategory}</td>
                           </tr>
                        ))}
                        <tr className="font-bold">
                           <td className="border border-black p-2 text-center">TOTAL</td>
                           <td className="border border-black p-2 text-center">{certificateData.totalTrees}</td>
                           <td className="border border-black p-2"></td>
                           <td className="border border-black p-2"></td>
                        </tr>
                     </>
                  ) : (
                     <tr>
                        <td className="border border-black p-2 text-center">-</td>
                        <td className="border border-black p-2 text-center">-</td>
                        <td className="border border-black p-2 text-center">-</td>
                        <td className="border border-black p-2 text-center">-</td>
                     </tr>
                  )}
               </tbody>
            </table>
         </div>

         {/* Conditions Section */}
         <div className="mb-8">
            <p className="mb-4">This permit is issued subject to the following conditions:</p>
            <ol className="list-decimal pl-6 space-y-3">
               <li>
                  Issues that may arise from the {certificateData?.requestType === 'Earth Balling' ? 'earth-balling' : 'tree cutting'} operation shall be addressed directly by the permittee;
               </li>
               <li>
                  Prior to {certificateData?.requestType === 'Earth Balling' ? 'earth-balling' : 'tree cutting'} operations, a placard or signboard measuring 4' x 8' shall be installed within the
                  area informing the public that the operation is authorized by the DENR and indicating
                  the name of the permittee, the purpose and the number of trees involved;
               </li>
               <li>
                  The operation shall, at all times, be under the direct supervision of the PENRO Marinduque, or
                  duly authorized representative(s);
               </li>
               <li>
                  The permittee shall be required to replace each tree to be {certificateData?.requestType === 'Earth Balling' ? 'earth-balled' : 'cut'} with fifty (50) seedlings of indigenous species;
               </li>
               <li>
                  The permittee shall undertake measures during and after operations to mitigate
                  the possible impacts of the said activity to the locality and the environment;
               </li>
               <li>
                  <span className="font-bold">
                     Violation of any of the terms and conditions of this permit shall be sufficient ground for
                     its termination/cancellation
                  </span>
                  , subject to due process without prejudice to the imposition
                  of appropriate penalties pursuant to existing regulations.
               </li>
            </ol>
         </div>

         {/* Validity Period */}
         <div className="mb-6">
            <p>
               This permit is valid for <span className="font-bold">Fifty (50) days</span> from the date of receipt
               or until the authorized number of trees have been {certificateData?.requestType === 'Earth Balling' ? 'earth-balled' : 'cut'}, whichever comes first.
            </p>
         </div>

         {/* Issuance Details */}
         <div className="mb-6">
            <p>
               Issued this {certificate?.dateIssued ? formatDate(certificate.dateIssued) : '_________________'},
               at DENR PENRO Marinduque, Capitol Compound, Brgy. Bangbangalon, Boac, Marinduque.
            </p>
         </div>

         {/* Signature Section */}
         <div className="mb-8">
            <p className="mb-4">By the Authority of the Secretary:</p>
            <div className="text-center mt-16">
               {certificate?.penrCenrOfficerSignature?.data && (
                  <div className="mb-2">
                     <img
                        src={`data:${certificate.penrCenrOfficerSignature.contentType};base64,${certificate.penrCenrOfficerSignature.data}`}
                        alt="PENR Officer Signature"
                        className="mx-auto h-16 object-contain"
                     />
                  </div>
               )}
               <p className="font-bold">IMELDA M. DIAZ</p>
               <p>OIC, PENR Officer</p>
            </div>
         </div>

         {/* Payment Details */}
         <div className="mb-4">
            <p>Amount paid: Php {orderOfPayment?.officialReceipt?.amount?.toFixed(2) || '_____________'}</p>
            <p>O.R. No.: {orderOfPayment?.officialReceipt?.orNumber || '_____________'}</p>
            <p>Date: {orderOfPayment?.officialReceipt?.dateIssued ? formatDate(orderOfPayment.officialReceipt.dateIssued) : '_____________'}</p>
         </div>

         {/* Footer */}
         <div className="text-xs">
            <p className="mb-4">***Not Valid without DENR PENRO Marinduque Official Dry Seal***</p>
         </div>

         {/* QR Code */}
         <div className={`absolute top-4 right-4 ${hiddenOnPrint.includes('qr-code') ? 'no-print' : ''}`}>
            <QRCode
               value={qrValue}
               size={80}
               style={{ height: "auto", maxWidth: "100%", width: "100%" }}
               viewBox={`0 0 80 80`}
            />
            <p className="text-xs mt-1 text-center">Scan to verify</p>
         </div>
      </div>
   );
});

TCEBPCertificateTemplate.displayName = 'TCEBPCertificateTemplate';

export default TCEBPCertificateTemplate;
